import { Injectable } from '@angular/core';
import { getCurrencySymbol } from '@angular/common';


/**
 * Singleton service to handle financial operations
 */
@Injectable()
export class FinancialUtilsService {

  private static nullableValue = '-';

  constructor() { }

  /**
   * Converts a large numerical value to a short version with the specific suffix
   * @param {number} value: entire initial value
   * @param toFixLowValue
   * @returns {string}: converted value
   */
  public static convertMoney(value: number, toFixLowValue?: number): string {
    if (value < 0) {
      return FinancialUtilsService.convertMoneyNegativeValues(value, toFixLowValue);
    }
    if (value >= 1000000000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value) / 1000000000000).toFixed(3)) :
        (Math.round(value) / 1000000000000).toFixed(toFixLowValue)) + 'T';
    } else if (value >= 1000000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000000000 * 100) / 100).toFixed(2)) :
        (Math.round(value / 1000000000 * 100) / 100).toFixed(toFixLowValue)) + 'B';
    } else if (value >= 1000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000000 * 100) / 100).toFixed(1)) :
        (Math.round(value / 1000000 * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value >= 1000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000 * 100) / 100).toFixed(1)) :
        (Math.round(value / 1000 * 100) / 100).toFixed(toFixLowValue)) + 'K';
    } else {
      return toFixLowValue ? value.toFixed(toFixLowValue).toString() : Math.round(value).toString();
    }
  }

  /**
   * Formats value for negative input
   * @param value
   * @param toFixLowValue
   */
  private static convertMoneyNegativeValues (value: number, toFixLowValue?: number): string {
    if (value <= -1000000000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value) / 1000000000000).toFixed(3)) :
        (Math.round(value) / 1000000000000).toFixed(toFixLowValue)) + 'T';
    } else if (value <= -1000000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000000000 * 100) / 100).toFixed(2)) :
        (Math.round(value / 1000000000 * 100) / 100).toFixed(toFixLowValue)) + 'B';
    } else if (value <= -1000000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000000 * 100) / 100).toFixed(1)) :
        (Math.round(value / 1000000 * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value <= -1000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / 1000 * 100) / 100).toFixed(1)) :
        (Math.round(value / 1000 * 100) / 100).toFixed(toFixLowValue)) + 'K';
    } else {
      return toFixLowValue ? value.toFixed(toFixLowValue).toString() : Math.round(value).toString();
    }
  }

  /**
   * Reduces the number using the {@Link convertMoney} adding the postfix letter if applies and adds the currency
   * @param {number} value
   * @param {string} currency
   * @param decimal
   * @returns {string}
   */
  public static convertMoneyCurrency (value: number, currency: string, decimal?: number): string {
    const currentSymbol = getCurrencySymbol(currency, 'wide');
    const spacer = currentSymbol.match(/[A-Z]/g) ? ' ' : '';
    const symbol = currentSymbol.replace(/[\w\s]/gi, '');
    const text = currentSymbol.replace(/[^\w\s]/gi, '');
    return `${text}${spacer}${symbol}${FinancialUtilsService.convertMoney(value, decimal)}`;
  }

  /**
   * Converts a large numerical value to a short version with the specific suffix using million suffix as min representation
   * @param {number} value: entire initial value
   * @param toFixLowValue
   * @returns {string}: converted value
   */
  public static convertMoneyMinMillion(value: number, toFixLowValue?: number): string {
    if (value < 0) {
      return FinancialUtilsService.convertMoneyNegativeMinMillionValues(value, toFixLowValue);
    }
    const trillion = 1000000000000;
    const billion = 1000000000;
    const million = 1000000;
    if (value >= trillion) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value) / trillion).toFixed(1)) :
        (Math.round(value) / trillion).toFixed(toFixLowValue)) + 'T';
    } else if (value >= billion) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / billion * 100) / 100).toFixed(1)) :
        (Math.round(value / billion * 100) / 100).toFixed(toFixLowValue)) + 'B';
    } else if (value >= million) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / million * 100) / 100).toFixed(0)) :
        (Math.round(value / million * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value >= 50000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / million * 100) / 100).toFixed(1)) :
        (Math.round(value / million * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value === 0) {
      return '0';
    } else {
      return '0M';
    }
  }

  /**
   * Formats value for negative input
   * @param value
   * @param toFixLowValue
   */
  private static convertMoneyNegativeMinMillionValues (value: number, toFixLowValue?: number): string {
    const trillion = 1000000000000;
    const billion = 1000000000;
    const million = 1000000;
    if (value <= -1 * trillion) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value) / trillion).toFixed(1)) :
        (Math.round(value) / trillion).toFixed(toFixLowValue)) + 'T';
    } else if (value <= -1 * billion) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / billion * 100) / 100).toFixed(1)) :
        (Math.round(value / billion * 100) / 100).toFixed(toFixLowValue)) + 'B';
    } else if (value <= -1 * million) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / million * 100) / 100).toFixed(0)) :
        (Math.round(value / million * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value <= -50000) {
      return (!toFixLowValue ?
        parseFloat((Math.round(value / million * 100) / 100).toFixed(1)) :
        (Math.round(value / million * 100) / 100).toFixed(toFixLowValue)) + 'M';
    } else if (value === 0) {
      return '0';
    } else {
      return '0M';
    }
  }

  /**
   * Reduces the number using the {@Link convertMoneyMinMillion} adding the postfix letter if applies and adds the currency
   * @param {number} value
   * @param {string} currency
   * @param decimal
   * @returns {string}
   */
  public static convertMoneyMinMillionCurrency (value: number, currency: string, decimal?: number): string {
    const currentSymbol = getCurrencySymbol(currency, 'wide');
    const spacer = currentSymbol.match(/[A-Z]/g) ? ' ' : '';
    const symbol = currentSymbol.replace(/[\w\s]/gi, '');
    const text = currentSymbol.replace(/[^\w\s]/gi, '');
    return `${text}${spacer}${symbol}${FinancialUtilsService.convertMoneyMinMillion(value, decimal)}`;
  }

  /**
   * Returns a value rounded in millions
   * @param {number} value
   * @returns {number}
   */
  public static roundInMillions (value: number): number {
    return Math.round(value * 100) / 100000000;
  }

  /**
   * Takes a value and returns it value by 1 million
   * @param {number} value
   * @returns {number}
   */
  public static formatInMillions (value: number): number {
    return value * 1000000;
  }

  /**
   * Used to return a formatted/reduced number with currency: 1000000 => $1M
   * @param {number} value
   * @param {string} currency
   * @param {number} decimals
   * @returns {string}
   */
  public static currencyNullConverter (value: number, currency?: string, decimals = 0): string {
    return value === null ? FinancialUtilsService.nullableValue : FinancialUtilsService.convertMoneyCurrency(value, currency, decimals);
  }

  /**
   * Returns the numerical value if exists, if not, returns a dash
   * @param {number} value
   * @param {number} decimals
   * @returns {string}
   */
  public static numberNullConverter (value: number, decimals = 0) {
    return value === null ? FinancialUtilsService.nullableValue : FinancialUtilsService.convertMoney(value);
  }

  /**
   * Returns the string value if exists, if null, returns a dash
   * @param {string} value
   * @returns {string}
   */
  public static stringNullConverter (value: string): string {
    return value === null ? FinancialUtilsService.nullableValue : value;
  }

  /**
   * Returns the string of the currency symbol
   * @param {string} currency
   * @returns {string}
   */
   public static getCurrencySymbol (currency: string): string {
    return getCurrencySymbol(currency, 'wide');
   }

}
